import { useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Prioritize WebP and AVIF in supported formats
const SUPPORTED_FORMATS = ['image/avif', 'image/webp', 'image/jpeg', 'image/png', 'image/gif'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const getExtensionFromMimeType = (mimeType: string): string => {
  switch (mimeType) {
    case 'image/avif': return 'avif';
    case 'image/webp': return 'webp';
    case 'image/jpeg': return 'jpg';
    case 'image/png': return 'png';
    case 'image/gif': return 'gif';
    default: return mimeType.split('/')[1];
  }
};

export function useFileUpload() {
  const [isUploading, setIsUploading] = useState(false);

  const uploadFile = useCallback(async (file: File): Promise<string | null> => {
    try {
      setIsUploading(true);
      console.log('Starting upload process for:', file.name);

      // Validate file type
      if (!SUPPORTED_FORMATS.includes(file.type)) {
        throw new Error('Unsupported file format. Please upload an AVIF, WebP, JPEG, PNG, or GIF image.');
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        throw new Error('File size exceeds 5MB limit.');
      }

      // Generate unique filename
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 7);
      const extension = getExtensionFromMimeType(file.type);
      const filename = `products/${timestamp}-${randomString}.${extension}`;

      console.log('Uploading file:', {
        originalName: file.name,
        generatedFilename: filename,
        type: file.type,
        size: file.size
      });

      // Upload to Supabase storage - use the file directly instead of ArrayBuffer
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(filename, file, {
          contentType: file.type,
          upsert: true,
          cacheControl: '3600'
        });

      if (uploadError) {
        console.error('Supabase upload error:', uploadError);
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      if (!uploadData?.path) {
        throw new Error('Upload successful but no path returned');
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('product-images')
        .getPublicUrl(uploadData.path);

      if (!urlData?.publicUrl) {
        throw new Error('Failed to get public URL for uploaded file');
      }

      console.log('Upload successful:', {
        path: uploadData.path,
        publicUrl: urlData.publicUrl
      });

      return urlData.publicUrl;
    } catch (error) {
      console.error('Upload process error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload file';
      toast.error(errorMessage);
      return null;
    } finally {
      setIsUploading(false);
    }
  }, []);

  return {
    uploadFile,
    isUploading
  };
} 